import {
  makeStyles,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableHeadProps as MuiTableHeadProps,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton,
  Collapse,
  Box,
  Paper,
  TableContainer,
} from "@material-ui/core"
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp"
import dayjs from "dayjs"
import { FC, MouseEvent, useState, useMemo, Fragment, useEffect } from "react"
import { getComparator, stableSort } from "../../utils/helpers"
import QuestionnaireScore from "./QuestionnaireScore"
import { colors } from "../../utils/theme"
import ChevronDownIcon from "../atoms/ChevronDownIcon"
import TableCell from "../atoms/TableCell"
import {
  AssessmentTableData,
  Order,
  QuestionnaireTakeByUser,
} from "../../utils/types"

interface Column {
  id: keyof AssessmentTableData
  label: string
}

type TableHeadProps = {
  order: Order
  orderBy: string
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof AssessmentTableData
  ) => void
} & MuiTableHeadProps

const TableHead: FC<TableHeadProps> = ({ order, orderBy, onRequestSort }) => {
  const classes = useStyles()
  const createSortHandler =
    (property: keyof AssessmentTableData) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  const getHeaderCellClassName = (columnId: keyof AssessmentTableData) => {
    if (columnId === "completeAt") return classes.dateHeaderCell
    if (columnId === "displayScore") return classes.scoreHeaderCell
    return classes.nameHeaderCell
  }

  const getSortLabelClassName = (columnId: keyof AssessmentTableData) => {
    if (columnId === "questionnaireName") return classes.tableSortAssessmentLbel
    return classes.tableSortLabel
  }

  const columns: Column[] = [
    {
      id: "completeAt",
      label: "Date",
    },
    {
      id: "questionnaireName",
      label: "Assessment",
    },
    {
      id: "displayScore",
      label: "Score",
    },
  ]
  return (
    <MuiTableHead>
      <TableRow>
        {columns.map((column) => (
          <MuiTableCell
            key={column.id}
            sortDirection={orderBy === column.id ? order : false}
            className={`${classes.tableHeaderCell} ${getHeaderCellClassName(
              column.id
            )}`}
            padding={column.label === "" ? "none" : undefined}
          >
            <TableSortLabel
              active={orderBy === column.id}
              direction={orderBy === column.id ? order : "asc"}
              onClick={createSortHandler(column.id)}
              IconComponent={(props) => <ChevronDownIcon {...props} />}
              classes={{
                root: getSortLabelClassName(column.id),
                active: getSortLabelClassName(column.id),
              }}
            >
              {column.label}
            </TableSortLabel>
          </MuiTableCell>
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

const Row: FC<{
  row: any
  openAssessment: boolean
}> = ({ row, openAssessment }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(openAssessment)
  }, [openAssessment])

  const classes = useStyles()

  return (
    <Fragment>
      <TableRow className={classes.root}>
        <TableCell className={classes.dateColumn}>
          {row.completeAt.data}
        </TableCell>
        <TableCell className={classes.nameColumn}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {row.questionnaireName.data}
        </TableCell>
        <TableCell role="button" align="center" className={classes.scoreColumn}>
          {row.displayScore.data != null && (
            <QuestionnaireScore
              score={row.displayScore.data}
              scoreType={row.displayScore.scoreType ?? ""}
              severityColor={row.severityColor.data}
              severityLabel={row.severityTitle.data}
              date={row.completeAt.data}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table
                size="small"
                aria-label="purchases"
                className={classes.subTable}
              >
                <TableBody>
                  <TableRow className={classes.subTableRow}>
                    <TableCell colSpan={2}>
                      <Typography variant="body1">Question</Typography>{" "}
                      {row.questionnaireResponses.data[0]?.questionnaire_item
                        .header ?? ""}
                    </TableCell>
                  </TableRow>
                  {row.questionnaireResponses.data.map(
                    (response: {
                      questionnaire_item: {
                        question: string
                      }
                      questionnaire_response_options: {
                        response_text: string
                      }
                    }) => (
                      // TODO: Add unique key (I don't have time to mock data to test this right now)
                      <>
                        <TableRow className={classes.subTableRow}>
                          <TableCell>
                            {response.questionnaire_item.question}
                          </TableCell>
                          <TableCell>
                            {
                              response.questionnaire_response_options
                                .response_text
                            }
                          </TableCell>
                        </TableRow>
                      </>
                    )
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  )
}

export default function AssessmentTable({
  assessmentData,
  openAssessment,
}: {
  assessmentData: QuestionnaireTakeByUser
  openAssessment: boolean
}) {
  const classes = useStyles()
  const [order, setOrder] = useState<Order>("desc")
  const [orderBy, setOrderBy] =
    useState<keyof AssessmentTableData>("completeAt")

  const tableData: AssessmentTableData[] = useMemo(
    () =>
      assessmentData.map((assessment) => ({
        completeAt: {
          sortData: assessment.completed_at,
          data: dayjs(assessment.completed_at).format("MM/DD/YYYY"),
        },
        questionnaireName: {
          sortData:
            assessment.questionnaireDisplayValues?.questionnaire_name ??
            assessment.questionnaire_schedule.questionnaire.name,
          data:
            assessment.questionnaireDisplayValues?.questionnaire_name ??
            assessment.questionnaire_schedule.questionnaire.name,
        },
        displayScore: {
          sortData: assessment.questionnaireDisplayValues?.display_score,
          data: assessment.questionnaireDisplayValues?.display_score,
          scoreType:
            assessment.questionnaireDisplayValues?.display_score_type ??
            undefined,
          containsRiskQuestions:
            assessment.questionnaire_schedule?.questionnaire
              .contains_risk_questions ?? false,
        },
        severityColor: {
          sortData:
            assessment.questionnaireDisplayValues?.scoring_display_color,
          data: assessment.questionnaireDisplayValues?.scoring_display_color,
        },
        severityTitle: {
          sortData: assessment.questionnaireDisplayValues?.scoring_display_name,
          data: assessment.questionnaireDisplayValues?.scoring_display_name,
        },
        completeStatus: {
          sortData: assessment.completion_status,
          data: assessment.completion_status,
        },
        questionnaireResponses: {
          sortData: assessment.questionnaire_responses,
          data: assessment.questionnaire_responses,
        },
      })),
    [assessmentData]
  )
  return (
    <>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table aria-label="collapsible table" className={classes.table}>
          <TableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={(
              event: MouseEvent<unknown>,
              property: keyof AssessmentTableData
            ) => {
              const isAsc = orderBy === property && order === "asc"
              setOrder(isAsc ? "desc" : "asc")
              setOrderBy(property)
            }}
          />
          <TableBody>
            {tableData.length > 0 &&
              stableSort(tableData, getComparator(order, orderBy)).map(
                (row, index) => (
                  <Row key={index} row={row} openAssessment={openAssessment} />
                )
              )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    overflowX: "unset",
  },
  table: {
    color: "var(--vira-ink, #1E263C)",
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    marginBottom: theme.spacing(2),
    "& .MuiTableCell-root": {
      fontWeight: 400,
      fontSize: 16,
      fontStyle: "normal",
      color: "var(--vira-ink, #1E263C)",
      borderColor: `var(--vira-border, ${colors.neutral.clay})`,
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    },
    "& .MuiTableCell-head": {
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
      color: "var(--vira-ink, #1E263C)",
    },
  },
  questionnaireColumn: {
    display: "flex",
    alignItems: "center",
  },
  tableHeaderCell: {
    color: "var(--vira-ink, #1E263C)",
    fontWeight: 700,
    borderBottom: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
  },
  dateHeaderCell: {
    width: 160,
  },
  nameHeaderCell: {
    width: "auto",
  },
  scoreHeaderCell: {
    width: 140,
  },
  dateColumn: {
    width: 160,
    height: 58,
  },
  nameColumn: {
    // Let the Assessment column take remaining space.
    width: "auto",
  },
  scoreColumn: {
    width: 140,
  },
  tableSortLabel: {
    color: "var(--vira-ink, #1E263C) !important",
    fontWeight: 700,
  },
  tableSortAssessmentLbel: {
    color: "var(--vira-ink, #1E263C) !important",
    fontWeight: 700,
    marginLeft: 10,
  },
  questionnaireScore: {
    height: 33,
    width: 33,
    borderRadius: 4,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  hightScore: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
  },
  lowScore: {
    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(6),
  },
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
  subTable: {
    marginLeft: 160,
    border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    width: "calc(100% - 160px)",
    backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
  },
  subTableRow: {
    height: 58,
    "& .MuiTypography-root": {
      fontWeight: "bold",
      display: "inline",
      color: "var(--vira-ink, #1E263C)",
    },
  },
  questionnaireScoreRiskAssessment: {
    height: 33,
    width: 95,
    borderRadius: 4,
    marginRight: theme.spacing(1),
  },
}))
